import { useEffect, useState } from "react";
import {
  ResponsiveContainer,
  BarChart,
  Bar,
  LineChart,
  Line,
  PieChart,
  Pie,
  Cell,
  XAxis,
  YAxis,
  Tooltip,
  CartesianGrid,
  Legend,
} from "recharts";
import {
  Buildings,
  Money,
  Percent,
  Warning,
  ArrowsLeftRight,
  TrendUp,
  Clock,
  FileText,
  Scales,
  Car,
} from "@phosphor-icons/react";
import { api } from "@/lib/api";
import { money, periodLabel } from "@/lib/format";
import { StatCard, PageHeader } from "@/components/common";

const COLORS = ["hsl(var(--chart-2))", "hsl(var(--chart-1))", "hsl(var(--chart-3))", "hsl(var(--chart-4))"];

function AlertList({ title, icon: Icon, items, render, tone = "text-amber-600" }) {
  return (
    <div className="bg-card border border-border rounded-sm p-4 h-full">
      <div className="flex items-center gap-2 mb-3">
        <Icon size={18} weight="duotone" className={tone} />
        <h3 className="text-sm font-semibold">{title}</h3>
        <span className="ml-auto text-xs font-bold tabular-nums bg-secondary rounded-sm px-2 py-0.5">
          {items?.length || 0}
        </span>
      </div>
      {!items || items.length === 0 ? (
        <p className="text-xs text-muted-foreground">Sin pendientes</p>
      ) : (
        <ul className="space-y-2 max-h-52 overflow-y-auto">
          {items.slice(0, 8).map((it, i) => (
            <li key={i} className="text-xs flex justify-between gap-2 border-b border-border pb-1.5 last:border-0">
              {render(it)}
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}

export default function Dashboard() {
  const [data, setData] = useState(null);

  useEffect(() => {
    api.get("/dashboard").then((r) => setData(r.data));
  }, []);

  if (!data) return <div className="text-muted-foreground">Cargando dashboard…</div>;
  const k = data.kpis;
  const c = data.charts;
  const a = data.alerts;

  return (
    <div data-testid="dashboard-page">
      <PageHeader title="Dashboard" subtitle="Operación inmobiliaria y flujo de dinero" />

      {/* KPIs */}
      <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 xl:grid-cols-7 gap-4 mb-4">
        <StatCard testId="kpi-flujo-neto" label="Flujo neto inmobiliaria" value={money(k.flujo_neto)} tone="primary" icon={TrendUp} />
        <StatCard testId="kpi-pendiente-transferir" label="Pendiente transferir" value={money(k.pendiente_transferir)} tone="warn" icon={ArrowsLeftRight} />
        <StatCard testId="kpi-comisiones-cobradas" label="Comisiones cobradas" value={money(k.comisiones_cobradas)} tone="accent" icon={Percent} />
        <StatCard testId="kpi-rentas-mes" label="Rentas del mes" value={money(k.rentas_mes)} icon={Money} />
        <StatCard testId="kpi-rentas-cobradas" label="Rentas cobradas (mes)" value={money(k.rentas_cobradas)} tone="accent" />
        <StatCard testId="kpi-rentas-pendientes" label="Rentas pendientes" value={money(k.rentas_pendientes)} tone="warn" />
        <StatCard testId="kpi-rentas-vencidas" label="Rentas vencidas" value={money(k.rentas_vencidas)} tone="danger" />
      </div>
      <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 xl:grid-cols-7 gap-4 mb-8">
        <StatCard testId="kpi-propiedades" label="Propiedades" value={k.propiedades_administradas} icon={Buildings} />
        <StatCard testId="kpi-ocupadas" label="Ocupadas" value={k.propiedades_ocupadas} tone="accent" />
        <StatCard testId="kpi-disponibles" label="Disponibles" value={k.propiedades_disponibles} />
        <StatCard testId="kpi-contratos" label="Contratos vigentes" value={k.contratos_vigentes} icon={FileText} />
        <StatCard testId="kpi-comisiones-generadas" label="Comisiones generadas" value={money(k.comisiones_generadas)} />
        <StatCard testId="kpi-gastos-prop" label="Gastos propiedades" value={money(k.gastos_propiedades)} />
        <StatCard testId="kpi-gastos-inmo" label="Gastos inmobiliaria" value={money(k.gastos_inmobiliaria)} />
      </div>

      {/* Charts */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-4 mb-4">
        <div className="lg:col-span-2 bg-card border border-border rounded-sm p-4">
          <h3 className="text-sm font-semibold mb-4">Cobranza mensual</h3>
          <ResponsiveContainer width="100%" height={260}>
            <BarChart data={c.cobranza_mensual}>
              <CartesianGrid strokeDasharray="3 3" stroke="hsl(var(--border))" vertical={false} />
              <XAxis dataKey="mes" tickFormatter={periodLabel} tick={{ fontSize: 11, fill: "hsl(var(--muted-foreground))" }} />
              <YAxis tick={{ fontSize: 11, fill: "hsl(var(--muted-foreground))" }} width={70} tickFormatter={(v) => money(v)} />
              <Tooltip formatter={(v) => money(v)} contentStyle={{ background: "hsl(var(--card))", border: "1px solid hsl(var(--border))", borderRadius: 4 }} />
              <Legend wrapperStyle={{ fontSize: 12 }} />
              <Bar dataKey="cobrado" name="Cobrado" fill="hsl(var(--chart-2))" radius={[3, 3, 0, 0]} />
              <Bar dataKey="pendiente" name="Pendiente" fill="hsl(var(--chart-3))" radius={[3, 3, 0, 0]} />
            </BarChart>
          </ResponsiveContainer>
        </div>
        <div className="bg-card border border-border rounded-sm p-4">
          <h3 className="text-sm font-semibold mb-4">Ocupación de inmuebles</h3>
          <ResponsiveContainer width="100%" height={260}>
            <PieChart>
              <Pie data={c.ocupacion.filter((o) => o.value > 0)} dataKey="value" nameKey="name" cx="50%" cy="50%" innerRadius={50} outerRadius={90} paddingAngle={2} label>
                {c.ocupacion.filter((o) => o.value > 0).map((e, i) => (
                  <Cell key={i} fill={COLORS[i % COLORS.length]} />
                ))}
              </Pie>
              <Tooltip contentStyle={{ background: "hsl(var(--card))", border: "1px solid hsl(var(--border))", borderRadius: 4 }} />
              <Legend wrapperStyle={{ fontSize: 12 }} />
            </PieChart>
          </ResponsiveContainer>
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4 mb-8">
        <div className="bg-card border border-border rounded-sm p-4">
          <h3 className="text-sm font-semibold mb-4">Ingresos por comisiones</h3>
          <ResponsiveContainer width="100%" height={240}>
            <LineChart data={c.ingresos_comisiones}>
              <CartesianGrid strokeDasharray="3 3" stroke="hsl(var(--border))" vertical={false} />
              <XAxis dataKey="mes" tickFormatter={periodLabel} tick={{ fontSize: 11, fill: "hsl(var(--muted-foreground))" }} />
              <YAxis tick={{ fontSize: 11, fill: "hsl(var(--muted-foreground))" }} width={70} tickFormatter={(v) => money(v)} />
              <Tooltip formatter={(v) => money(v)} contentStyle={{ background: "hsl(var(--card))", border: "1px solid hsl(var(--border))", borderRadius: 4 }} />
              <Line type="monotone" dataKey="comisiones" name="Comisiones" stroke="hsl(var(--chart-1))" strokeWidth={2.5} dot={{ r: 3 }} />
            </LineChart>
          </ResponsiveContainer>
        </div>
        <div className="bg-card border border-border rounded-sm p-4">
          <h3 className="text-sm font-semibold mb-4">Dinero administrado por propietario</h3>
          <ResponsiveContainer width="100%" height={240}>
            <BarChart data={c.dinero_por_propietario} layout="vertical">
              <CartesianGrid strokeDasharray="3 3" stroke="hsl(var(--border))" horizontal={false} />
              <XAxis type="number" tick={{ fontSize: 11, fill: "hsl(var(--muted-foreground))" }} tickFormatter={(v) => money(v)} />
              <YAxis type="category" dataKey="nombre" width={110} tick={{ fontSize: 11, fill: "hsl(var(--muted-foreground))" }} />
              <Tooltip formatter={(v) => money(v)} contentStyle={{ background: "hsl(var(--card))", border: "1px solid hsl(var(--border))", borderRadius: 4 }} />
              <Bar dataKey="saldo" name="Saldo" fill="hsl(var(--chart-2))" radius={[0, 3, 3, 0]} />
            </BarChart>
          </ResponsiveContainer>
        </div>
      </div>

      {/* Alerts */}
      <h2 className="text-lg font-display font-bold mb-3">Alertas</h2>
      <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-4 gap-4">
        <AlertList title="Rentas por vencer" icon={Clock} items={a.rentas_por_vencer} render={(it) => (<><span>{it.inmueble} · {periodLabel(it.periodo)}</span><span className="tabular-nums">{money(it.importe)}</span></>)} />
        <AlertList title="Rentas vencidas" icon={Warning} tone="text-red-600" items={a.rentas_vencidas} render={(it) => (<><span>{it.inmueble} · {periodLabel(it.periodo)}</span><span className="tabular-nums text-red-600">{money(it.importe)}</span></>)} />
        <AlertList title="Contratos por vencer" icon={FileText} items={a.contratos_por_vencer} render={(it) => (<><span>{it.inmueble}</span><span className="text-muted-foreground">{it.fecha_fin}</span></>)} />
        <AlertList title="Transferencias pendientes" icon={ArrowsLeftRight} items={a.transferencias_pendientes} render={(it) => (<><span>{it.propietario}</span><span className="tabular-nums">{money(it.saldo)}</span></>)} />
      </div>
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mt-4">
        <AlertList title="Expedientes por caducar" icon={Scales} tone="text-red-600" items={a.expedientes_por_caducar} render={(it) => (<><span>Exp. {it.numero} · {it.materia || ""}</span><span className={it.dias < 0 ? "text-red-600 font-semibold" : "text-amber-600 font-semibold"}>{it.dias < 0 ? `caducó hace ${-it.dias}d` : `${it.dias}d`}</span></>)} />
        <AlertList title="Verificaciones / seguros de vehículos" icon={Car} items={a.vehiculos_vencimientos} render={(it) => (<><span>{it.vehiculo} · {it.tipo}</span><span className={it.dias < 0 ? "text-red-600 font-semibold" : "text-amber-600 font-semibold"}>{it.dias < 0 ? `venció hace ${-it.dias}d` : `${it.dias}d`}</span></>)} />
      </div>
    </div>
  );
}
